import { useState, useEffect, useMemo } from "react";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { ROAST_ATTR_TAXONOMY } from "@/constants/common";
import { SortDirection, SortOptions } from "@/constants/enums";
import type { GetProductListRequestParams } from "@/types/api/wooStoreApi";
import {
  StockStatus,
  type AttributeTerm,
  type Product,
  type ProductAttribute,
  type ProductCategory,
} from "@/types/products";

//#region Type
interface UseRelatedProductsReturn {
  relatedProducts: Product[] | null;
  isRelatedProductsLoading: boolean;
  relatedCategory: ProductCategory | null;
  relatedAttrAndTerm: RelatedAttrAndTerm | null;
  relatedFilter: RelatedFilter;
  filteredRelatedProducts: Product[] | null;
  updateRelatedFilter: (filter: RelatedFilter) => void;
}

interface RelatedAttrAndTerm {
  attr: ProductAttribute;
  term: AttributeTerm;
}
interface RelatedFilter {
  categoryId?: number;
  attribute?: { attrId: number; termId: number };
}
//#endregion

//#region helper
const buildParams = (
  relatedCategory: ProductCategory | null,
  relatedAttrAndTerm: RelatedAttrAndTerm | null,
): GetProductListRequestParams => ({
  per_page: 8,
  orderby: SortOptions.DATE,
  order: SortDirection.ASC, // 舊到新，故意和商品列表相反，避免和商品列表過於重複相似，也增加較舊商品曝光機會
  stock_status: [StockStatus.InStock],
  category: relatedCategory?.slug,
  attributes: relatedAttrAndTerm
    ? [{ attribute: relatedAttrAndTerm.attr.taxonomy, terms: relatedAttrAndTerm.term.slug }]
    : undefined,
});
//#endregion

// 相關商品的篩選條件：使用商品分類中的最後一個子分類，或烘焙度屬性中的第一個 term 作為條件
const useRelatedProducts = ({ product }: { product: Product | null }): UseRelatedProductsReturn => {
  const appDispatch = useAppDispatch();

  const [products, setProducts] = useState<Product[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<RelatedFilter>({});

  const lastCategory: ProductCategory | null = useMemo(
    () => product?.categories?.[product.categories.length - 1] ?? null, // API 回傳 categories 已由父到子排序，取最後一個作為最細分類
    [product],
  );

  const firstRoastAttribute: RelatedAttrAndTerm | null = useMemo(() => {
    const roastAttr = product?.attributes.find((attr) => attr.taxonomy === ROAST_ATTR_TAXONOMY);
    return roastAttr?.terms[0] ? { attr: roastAttr, term: roastAttr?.terms[0] } : null; // 商品通常只設定一個烘焙度，故取第一個
  }, [product]);

  const filteredProducts: Product[] | null = useMemo(() => {
    if (!products?.length) return [];

    if (filter?.categoryId) {
      const filterResult = products?.filter(({ categories }) =>
        categories.some((category) => category.id === filter.categoryId),
      );

      return filterResult;
    }

    if (filter?.attribute) {
      const filterResult = products?.filter(({ attributes }) => {
        return attributes.some(
          (attr) =>
            attr.id === filter.attribute?.attrId &&
            attr.terms.some((term) => term.id === filter.attribute?.termId),
        );
      });

      return filterResult;
    }

    return [];
  }, [products, JSON.stringify(filter)]); // (依賴陣列內的物件使用淺比較，比較物件的 reference 記憶體位置，故改轉為字串來比較內容)

  const updateFilter = (nextFilter: RelatedFilter) => {
    setFilter(nextFilter);
  };

  const fetchRelatedProducts = async (signal?: AbortSignal) => {
    setIsLoading(true);
    const params = buildParams(lastCategory, firstRoastAttribute);

    try {
      const { data } = await wooStoreApiService.product.getList(params, signal);
      setProducts(data);
    } catch (error) {
      handleApiError(error, appDispatch, "取得相關商品失敗，請稍後再試");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!product) return;
    const controller = new AbortController();
    fetchRelatedProducts(controller.signal);

    return () => {
      controller.abort();
    };
  }, [product]);

  return {
    relatedProducts: products,
    isRelatedProductsLoading: isLoading,

    relatedCategory: lastCategory,
    relatedAttrAndTerm: firstRoastAttribute,
    relatedFilter: filter,
    filteredRelatedProducts: filteredProducts,
    updateRelatedFilter: updateFilter,
  };
};

export default useRelatedProducts;
